import React, { useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Delete } from '@material-ui/icons';
import { parseISO, format } from 'date-fns';

import { UnidataTable } from '~/components';
import { DialogConfirmation } from '~/componentsLocal';
import {
  CENTRO_CUSTO_ABASTECIMENTO,
  MODULO_20_GRAUS,
  TRR_CHILE,
} from '~/constants/modules';
import { t, can, number } from '~/helpers';
import { handleExportGeneric } from '~/helpers/exportReports';
import {
  deleteOperacaoAbastecimento,
  findOperacaoAbastecimento,
  recordFiltering,
} from '~/store/modules/operacaoabastecimento/actions';

import Form from './form';
import FormDeletar from './formDeletar';

function OperacaoAbastecimentoList() {
  const { internounidatausuario } = useSelector((state) => state.user.configs);

  const redux = useSelector((state) => state.operacaoabastecimento);

  const deleteRef = useRef(null);

  const buttons = useMemo(
    () =>
      internounidatausuario.toLowerCase() === 's'
        ? [
            {
              title: t('s.geral.botaoexcluir'),
              action: (value) => deleteRef.current.handleOpen(value),
              icon: <Delete />,
            },
          ]
        : [],
    [internounidatausuario]
  );

  const header = useMemo(() => {
    const data = [
      {
        id: 'datahorainicialmovimentacao',
        label: t('s.operacao.abastecimento.datahorainicialmovimentacao'),
        formatted: (value) => format(parseISO(value), 'dd/MM/yyyy HH:mm'),
        filter: {
          type: 'initialdatetime',
          defaultValue: format(new Date(), 'dd/MM/yyyy 00:00:00'),
        },
      },
      {
        id: 'datahorafinalmovimentacao',
        label: t('s.operacao.abastecimento.datahorafinalmovimentacao'),
        formatted: (value) => format(parseISO(value), 'dd/MM/yyyy HH:mm'),
        filter: {
          type: 'finaldatetime',
          defaultValue: format(new Date(), 'dd/MM/yyyy 23:59:59'),
        },
      },
      {
        id: 'codigonomeposto',
        label: t('s.operacao.abastecimento.nomeposto'),
        filter: { type: 'text' },
      },
      {
        id: 'idbico',
        label: t('s.operacao.abastecimento.idbico'),
        filter: { type: 'number' },
        align: 'left',
      },
      {
        id: 'nomeproduto',
        label: t('s.operacao.abastecimento.nomeproduto'),
        filter: { type: 'text' },
      },
      {
        id: 'volumemovimentacao',
        label: t('s.operacao.abastecimento.volumemovimentacao'),
        align: 'right',
        formatted: (value) => number(value, 2),
      },
    ];

    if (!can(TRR_CHILE)) {
      data.splice(5, 0, {
        id: 'idveiculo',
        label: t('s.operacao.abastecimento.idveiculo'),
        filter: { type: 'text' },
      });
      data.splice(6, 0, {
        id: 'totalizadormovimentacaoabastecimento',
        label: t(
          's.operacao.abastecimento.totalizadormovimentacaoabastecimento'
        ),
        align: 'right',
        formatted: (value) => number(value, 2),
      });
      data.splice(7, 0, {
        id: 'totalizador2movimentacaoabastecimento',
        label: t('s.cadastro.veiculo.totalizador2veiculo'),
        align: 'right',
        formatted: (value) => number(value, 2),
      });
    }
    if (can(TRR_CHILE)) {
      data.splice(5, 0, {
        id: 'codigonomeempresaveiculo',
        label: t('s.cadastro.empresacliente'),
        filter: { type: 'text' },
      });
    }

    if (can(CENTRO_CUSTO_ABASTECIMENTO)) {
      data.splice(5, 0, {
        id: 'idcentrocusto',
        label: t('s.operacao.abastecimento.idcentrocusto'),
        filter: { type: 'text' },
      });
    }

    if (can(MODULO_20_GRAUS)) {
      data.splice(9, 0, {
        id: 'densidade20movimentacao',
        label: t('s.operacao.abastecimento.densidade20'),
        align: 'right',
        formatted: (value) => number(value, 4),
      });

      data.splice(10, 0, {
        id: 'temperaturamediamovimentacao',
        label: t('s.operacao.abastecimento.temperaturamedia'),
        align: 'right',
        formatted: (value) => number(value, 1),
      });

      data.splice(11, 0, {
        id: 'volume20movimentacao',
        label: t('s.operacao.abastecimento.volume20movimentacao'),
        align: 'right',
        formatted: (value) => number(value, 2),
      });
    }

    return data;
  }, []);

  const fieldlist = [
    'datahorainicialmovimentacao',
    'datahorafinalmovimentacao',
    'codigonomeposto',
    'idbico',
    'nomeproduto',
    'volumemovimentacao',
    'idveiculo',
    'totalizadormovimentacaoabastecimento',
    'totalizador2movimentacaoabastecimento',
    'encerranteinicialmovimentacao',
    'encerrantefinalmovimentacao',
  ];

  const substituirHeader = {
    datahorainicialmovimentacao: t(
      's.operacao.abastecimento.datahorainicialmovimentacao'
    ),
    datahorafinalmovimentacao: t(
      's.operacao.abastecimento.datahorafinalmovimentacao'
    ),
    codigonomeposto: t('s.operacao.abastecimento.nomeposto'),
    idbico: t('s.operacao.abastecimento.idbico'),
    nomeproduto: t('s.operacao.abastecimento.nomeproduto'),
    volumemovimentacao: t('s.operacao.abastecimento.volumemovimentacao'),
    idveiculo: t('s.operacao.abastecimento.idveiculo'),
    totalizadormovimentacaoabastecimento: t(
      's.operacao.abastecimento.totalizadormovimentacaoabastecimento'
    ),
    totalizador2movimentacaoabastecimento: t(
      's.cadastro.veiculo.totalizador2veiculo'
    ),
    encerranteinicialmovimentacao: t(
      's.operacao.abastecimento.encerranteinicialmovimentacao'
    ),
    encerrantefinalmovimentacao: t(
      's.operacao.abastecimento.encerrantefinalmovimentacao'
    ),
  };

  if (can(TRR_CHILE)) {
    fieldlist.push('codigonomeempresaveiculo');
    substituirHeader.codigonomeempresaveiculo = t('s.cadastro.empresacliente');
  }

  if (can(CENTRO_CUSTO_ABASTECIMENTO)) {
    fieldlist.push('idcentrocusto');
    substituirHeader.idcentrocusto = t(
      's.operacao.abastecimento.idcentrocusto'
    );
  }

  if (can(MODULO_20_GRAUS)) {
    fieldlist.push('densidade20movimentacao');
    substituirHeader.densidade20movimentacao = t(
      's.operacao.abastecimento.densidade20'
    );
    fieldlist.push('temperaturamediamovimentacao');
    substituirHeader.temperaturamediamovimentacao = t(
      's.operacao.abastecimento.temperaturamedia'
    );
    fieldlist.push('volume20movimentacao');
    substituirHeader.volume20movimentacao = t(
      's.operacao.abastecimento.volume20movimentacao'
    );
  }

  return (
    <>
      <UnidataTable
        fixed
        actions={buttons}
        redux={redux}
        header={header}
        read={recordFiltering}
        find={findOperacaoAbastecimento}
        delete={internounidatausuario.toLowerCase() !== 's'}
        remove={deleteOperacaoAbastecimento}
        form={Form}
        formAux={FormDeletar}
        keys={['idmovimentacao', 'idposto']}
        detach="nomeposto"
        pdf
        excel
        onExport={(formato) =>
          handleExportGeneric({
            endpoint: 'movimentacoesabastecimentos',
            formato,
            fields: fieldlist,
            headers: substituirHeader,
            redux,
            ordination: redux.params.ordination,
            orderfield: redux.params.orderfield,
            title: 'Relatório de Abastecimentos',
          })
        }
      />

      <DialogConfirmation
        ref={deleteRef}
        keys={['idmovimentacao', 'idposto']}
        title="Justificativa para exclusão de registro"
        redux={deleteOperacaoAbastecimento}
        query={redux.params}
      />
    </>
  );
}

export default OperacaoAbastecimentoList;
